import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, MapPin, ArrowRight, Ticket } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function MyBookings() {
  const { user, loading, login } = useAuth();
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/bookings/me");
        setBookings(data);
      } catch {
        setBookings([]);
      }
    })();
  }, [user]);

  if (loading) {
    return (
      <main className="pt-32 pb-16 min-h-screen text-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#1F4D3A]/20 border-t-[#1F4D3A] animate-spin mx-auto" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="pt-32 pb-24 min-h-screen paper-texture">
        <section className="max-w-2xl mx-auto text-center px-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F5F0E8] mb-6">
            <Ticket className="w-7 h-7 text-[#C87A50]" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1F4D3A] mb-3">Track your trips.</h1>
          <p className="text-[#4A3728] text-lg mb-8">Log in to see the bookings you've submitted.</p>
          <Button
            data-testid="bookings-login-cta"
            onClick={login}
            className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full px-8 h-12"
          >
            Sign in with Google <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </section>
      </main>
    );
  }

  return (
    <main className="pt-24 sm:pt-32 pb-16 paper-texture min-h-screen">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 sm:mb-14">
          <span className="leaf-divider inline-flex">Trip history</span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-[#1F4D3A] mt-3">My bookings</h1>
          <p className="text-[#4A3728] mt-3 max-w-2xl">
            Every enquiry you've sent us — a captain will reach out within 24 hours.
          </p>
        </div>

        {bookings === null ? (
          <div className="w-10 h-10 rounded-full border-2 border-[#1F4D3A]/20 border-t-[#1F4D3A] animate-spin mx-auto" />
        ) : bookings.length === 0 ? (
          <div className="bg-white border border-[#E5DDCB] rounded-3xl p-10 sm:p-14 text-center" data-testid="bookings-empty">
            <Ticket className="w-10 h-10 text-[#C87A50] mx-auto mb-4" />
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-2">No bookings yet.</h2>
            <p className="text-[#5A4A38] mb-6">Pick a road story and we'll get you rolling.</p>
            <Link to="/tours">
              <Button className="btn-primary bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full px-8 h-12">
                Explore tours <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white border border-[#E5DDCB] rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <span className="inline-block text-[10px] uppercase tracking-widest bg-[#F5F0E8] text-[#1F4D3A] px-2 py-1 rounded-full font-semibold mb-2">
                    {b.status}
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-[#1F4D3A]">{b.tour_title}</h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-xs sm:text-sm text-[#5A4A38]">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#C87A50]" /> {b.date}</span>
                    <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#C87A50]" /> {b.travellers} traveller{b.travellers > 1 ? "s" : ""}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C87A50]" /> Enquired {new Date(b.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <Link to={`/tours/${b.tour_id}`}>
                  <Button variant="outline" className="rounded-full border-[#1F4D3A] text-[#1F4D3A] hover:bg-[#1F4D3A] hover:text-white">
                    View trip <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
